import { humanize } from 'inflected';
import { filterTokenizer, Token } from '@blisspointmedia/bpm-types/dist/advancedFilterParser';
import { makeFilter } from '@blisspointmedia/bpm-types/dist/booleanFilterParserCreator';
import { Category, FilterPaneState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import {
  ROAS_FORMATTER,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyPercent2Decimals,
  VOLUME_FORMATTER
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { conditionallyPluralizeWithCount } from '../../utils/text-utils';
import {
  Alert,
  AlertCategory,
  ColorMode,
  ColorModeType,
  DiversityAdSet,
  DiversityLabel,
  FatigueAdSet,
  DiversityLabelType,
  FatiguePoint,
  LaunchEvent,
  PlatformSpread,
  PlatformTier,
  PlatformTierType,
  QuadrantPoint,
  Ad,
  AdSet
} from './meta-fatigue-insights-constants';
import {
  Danger500,
  Danger900,
  getSteppedHeatColor,
  hexToRgb,
  Neutral500,
  Neutral700,
  SequentialGreenRamp,
  Success500,
  Success900,
  Warning600,
  Warning900
} from '../../utils/colors';

// Shared by meta-fatigue-data.ts (which computes these figures for the whole account) and
// by the filter path below (which recomputes them for a pruned ad list). One definition,
// so a filtered share can never be rounded differently from an unfiltered one.
export const round = (v: number, p = 4): number => Number(v.toFixed(p));

// ── Derived view-models ────────────────────────────────────────────────────
// Returns only the ad sets with a fatigue series, for the fatigue chart/quadrant.
// Generic so callers can compose it with getDiversityAdSets below without losing the
// narrower DiversityAdSet type (buildQuadrantPoints needs both filters at once).
export const getFatigueAdSets = <T extends AdSet>(adSets: T[]): T[] =>
  adSets.filter(adSet => adSet.fatigueSeries.length > 0);

// Returns only ad sets with a real diversity score/label — a fatigue-only ad set (NA
// diversity row) has neither. This is the single place that decides what's eligible for
// the Diversity tab, diversity alerts, and the quadrant chart's x-axis; every other view
// (Combined, Fatigue) keeps consuming the full, unscoped AdSet[] list.
export const getDiversityAdSets = (adSets: AdSet[]): DiversityAdSet[] =>
  adSets.filter(
    (adSet): adSet is DiversityAdSet =>
      adSet.diversityScore !== null && adSet.diversityLabel !== null
  );

// ── Filter pane ──────────────────────────────────────────────────────────────
// a set matches if ANY of its values is checked.
const FILTER_CATEGORY_VALUES: Record<string, (adSet: AdSet) => string[]> = {
  account: adSet => [adSet.accountName],
  campaign: adSet => [adSet.campaign],
  adSet: adSet => [adSet.name],
  ad: adSet => adSet.ads.map(ad => ad.name),
  diversity: adSet => (adSet.diversityLabel === null ? [] : [adSet.diversityLabel])
};

// Categories that describe an AD rather than an ad set. These do two jobs: they decide
// whether the parent ad set survives at all (via FILTER_CATEGORY_VALUES above, which
// keeps the set when ANY of its ads match) and they prune the surviving set's ad list
// down to the ads that actually matched. Without the second job an "Ad" filter would
// keep the whole set and still render every ad in it.
const AD_LEVEL_CATEGORY_VALUES: Record<string, (ad: Ad) => string[]> = {
  ad: ad => [ad.name]
};

const checkedOptionsFor = (filterState: FilterPaneState, key: string): string[] => {
  const selected = filterState.basic.selectedMap[key] ?? {};
  return Object.keys(selected).filter(option => selected[option]);
};

const matchesCategory = (
  adSet: AdSet,
  category: Category,
  filterState: FilterPaneState
): boolean => {
  const checkedOptions = checkedOptionsFor(filterState, category.key);
  if (checkedOptions.length === 0) return true;
  const getValues = FILTER_CATEGORY_VALUES[category.key];
  if (!getValues) return true; // unknown category key — fail open rather than hide everything
  const matchesAny = getValues(adSet).some(value => checkedOptions.includes(value));
  return filterState.basic.notMap[category.key] ? !matchesAny : matchesAny;
};

// ── Ad-level pruning ─────────────────────────────────────────────────────────

const visibleAdsFor = (
  adSet: AdSet,
  categories: Category[],
  filterState: FilterPaneState
): Ad[] => {
  const activeAdCategories = categories
    .map(category => ({
      key: category.key,
      getValues: AD_LEVEL_CATEGORY_VALUES[category.key],
      checkedOptions: checkedOptionsFor(filterState, category.key)
    }))
    .filter(entry => entry.getValues !== undefined && entry.checkedOptions.length > 0);
  if (activeAdCategories.length === 0) return adSet.ads;
  return adSet.ads.filter(ad =>
    activeAdCategories.every(({ key, getValues, checkedOptions }) => {
      const matchesAny = getValues(ad).some(value => checkedOptions.includes(value));
      return filterState.basic.notMap[key] ? !matchesAny : matchesAny;
    })
  );
};

// How many top-spend creatives carry >=80% of the spend across `ads`. Exported so that
// meta-fatigue-data.ts computes it once for the unfiltered account and this file
// recomputes it identically for a filtered ad list — one rule, not two that can drift.
export const CONCENTRATION_THRESHOLD = 0.8;

export const computeConcentration = (
  shares: number[]
): { creativesDriving: number; spendShare: number } => {
  let cumulative = 0;
  let creativesDriving = 0;
  for (const share of shares) {
    if (cumulative >= CONCENTRATION_THRESHOLD) break;
    cumulative += share;
    creativesDriving += 1;
  }
  creativesDriving = Math.max(1, creativesDriving);
  const spendShare = round(
    shares.slice(0, creativesDriving).reduce((sum, share) => sum + share, 0)
  );
  return { creativesDriving, spendShare };
};

// Rebuilds the three fields that describe an ad set's ad list — activeAds,
// creativesDriving and spendShare — plus each ad's own spendShare, against the ads that
// survived filtering. Without this, a filtered view reports "8 ads" and a 94% share taken
// from the unfiltered account while showing two rows, which reads as a bug.
//
// Each ad's share is recomputed against the VISIBLE spend total rather than the ad set's
// real spend, so the shares still sum to 1 in the filtered view. The money columns
// (spend/impressions/clicks and the derived rates) are deliberately left alone: those are
// account truth for the ad set and do not become wrong just because a row is hidden.
export const recomputeAdSetFromAds = (adSet: AdSet, ads: Ad[]): AdSet => {
  if (ads.length === adSet.ads.length) return adSet;
  const visibleSpend = ads.reduce((sum, ad) => sum + ad.spend, 0);
  const rescaledAds = ads
    .map(ad => ({
      ...ad,
      spendShare: round(visibleSpend > 0 ? ad.spend / visibleSpend : 0)
    }))
    .sort((a, b) => b.spend - a.spend);
  const { creativesDriving, spendShare } = computeConcentration(
    rescaledAds.map(ad => ad.spendShare)
  );
  return {
    ...adSet,
    ads: rescaledAds,
    activeAds: rescaledAds.length,
    creativesDriving,
    spendShare
  };
};

// ── Advanced filter ──────────────────────────────────────────────────────────
// The advanced pane hands over a raw where-clause string. Evaluating it in the browser —
// rather than compiling it to SQL and refetching, as the server-side pages do — keeps this
// page's "filter what we already downloaded" model intact.
//
// Both halves of this are existing shared machinery, composed rather than reimplemented:
//   filterTokenizer  — FetchFilterBar's OWN tokenizer for this exact grammar. It handles
//                      double-quoted multi-word values and multi-word category names.
//   makeFilter       — the codebase's client-side predicate builder (BasicFilterBar,
//                      AdvancedFilterBar, LabelingRulesModal, BillingAutomation,
//                      StreamingCreatives, SegmentationLabeling all use it). It owns the
//                      and/or precedence, the parentheses and the comparison operators.
//
// The only thing missing between them is that the two carry different token vocabularies,
// so ADVANCED_TOKEN_TYPES / ADVANCED_OPS below are a straight rename. Nothing is parsed
// here.
const ADVANCED_TOKEN_TYPES: Record<string, string> = {
  cat: 'cat',
  comp: 'op',
  val: 'val',
  logic: 'bool',
  '(': 'openParen',
  ')': 'closeParen'
};

const ADVANCED_OPS: Record<string, string> = {
  '=': 'is',
  '!=': 'is not',
  '~': 'is like',
  '!~': 'is not like',
  like: 'is like'
};

interface AdvancedToken {
  type: string;
  key: string;
}

// Throws on anything the tokenizer emits that makeFilter has no equivalent for: an
// 'unknown' token from a half-typed clause, an operator outside the table above, or a
// category this page does not have. filterAdSetsAdvanced turns every one of those into
// fail-open.
//
// The unknown-category check is load bearing. Without it makeFilter falls back to its
// defaultOptionGetter, which reads `row[name]` — so a typo resolves to undefined and the
// clause silently matches NOTHING, showing an empty page as though the filter were valid.
const toMakeFilterTokens = (tokens: Token[], categoryNames: Set<string>): AdvancedToken[] =>
  tokens.map(token => {
    const type = ADVANCED_TOKEN_TYPES[token.type];
    if (!type) throw new Error(`Unsupported token type "${token.type}".`);
    if (token.type === 'comp') {
      const op = ADVANCED_OPS[token.value.toLowerCase()];
      if (!op) throw new Error(`Unsupported operator "${token.value}".`);
      return { type, key: op };
    }
    if (token.type === 'logic') return { type, key: token.value.toLowerCase() };
    if (token.type === 'cat' && !categoryNames.has(token.value)) {
      throw new Error(`Unknown filter category "${token.value}".`);
    }
    return { type, key: token.value };
  });

// makeFilter reads a row through each category's optionsGetter, and its leaf evaluator ORs
// across the returned array. FILTER_CATEGORY_VALUES already has exactly that shape, so the
// basic and advanced panes read an ad set through the same getters — one definition, so the
// two panes cannot disagree about what "campaign" means.
//
// Registered under the DISPLAY LABEL, because that is what the pane's own suggestion
// inserter writes: picking "Ad set" produces `"Ad set" = "..."`, so the cat token carries
// the label, not the internal key. The key is registered too, as an alias, so a
// hand-typed `adSet = "..."` also resolves.
//
// Two consequences of the OR-across-values rule are worth knowing: `"Ad" = "Foo"` matches
// an ad set when ANY of its ads is Foo (the same grain the basic pane filters at), and by
// the same token `"Ad" != "Foo"` matches when ANY ad is not Foo, not when NONE is.
const advancedCategories = (categories: Category[]) =>
  categories
    .filter(category => FILTER_CATEGORY_VALUES[category.key] !== undefined)
    .flatMap(category => {
      // makeFilter is generic over the row shape, so it types this argument as the loosest
      // thing a row could be. It passes whatever row we hand the predicate straight through
      // untouched, and the only call is `predicate(adSet)` below — so narrowing back to
      // AdSet is a single library-boundary cast, not an assumption about the data (the same
      // exception QuadrantChart's ScatterShapeProps payload takes).
      const optionsGetter = (row: Record<string, unknown>) =>
        FILTER_CATEGORY_VALUES[category.key](row as unknown as AdSet);
      const names =
        category.label === category.key ? [category.key] : [category.label, category.key];
      return names.map(name => ({ name, label: category.label, optionsGetter }));
    });

const filterAdSetsAdvanced = (
  adSets: AdSet[],
  categories: Category[],
  filterState: FilterPaneState
): AdSet[] => {
  const clause = filterState.advanced?.trim();
  if (!clause) return adSets;
  try {
    const resolvedCategories = advancedCategories(categories);
    const predicate = makeFilter<AdSet>(
      toMakeFilterTokens(
        filterTokenizer(clause),
        new Set(resolvedCategories.map(category => category.name))
      ),
      resolvedCategories
    );
    return adSets.filter(adSet => predicate(adSet));
  } catch {
    // A half-typed clause is the normal state while someone is typing, and both the token
    // rename and makeFilter throw on one. Fail OPEN: showing everything is honest, whereas
    // an empty page would read as "no ad sets match" when nothing was ever evaluated.
    return adSets;
  }
};

// Advanced mode does NOT prune the ad list the way the basic "Ad" category does: the token
// stream gives no way to attribute a match back to a single ad.
export const filterAdSets = (
  adSets: AdSet[],
  categories: Category[],
  filterState: FilterPaneState
): AdSet[] => {
  if (filterState.isAdvanced) {
    return filterAdSetsAdvanced(adSets, categories, filterState);
  }
  const kept: AdSet[] = [];
  for (const adSet of adSets) {
    if (!categories.every(category => matchesCategory(adSet, category, filterState))) continue;
    kept.push(recomputeAdSetFromAds(adSet, visibleAdsFor(adSet, categories, filterState)));
  }
  return kept;
};

// either "your filters excluded everything" or a specific "there's no data here" message,
// to make it clear why a chart/table is blank.
export const emptyStateMessage = (filtersActive: boolean, whenNoFilters: string): string =>
  filtersActive ? 'No ad sets match your current filters.' : whenNoFilters;

// ── Diversity severity (from the data's own LOW/MEDIUM/HIGH label) ───────────
const DiversityLevel = {
  DANGER: 'danger',
  WARN: 'warn',
  OK: 'ok'
} as const;
export type DiversityLevelType = (typeof DiversityLevel)[keyof typeof DiversityLevel];

const LABEL_LEVEL: Record<DiversityLabelType, DiversityLevelType> = {
  [DiversityLabel.LOW]: DiversityLevel.DANGER,
  [DiversityLabel.MEDIUM]: DiversityLevel.WARN,
  [DiversityLabel.HIGH]: DiversityLevel.OK
};

export const getDiversityLevel = (label: DiversityLabelType): DiversityLevelType =>
  LABEL_LEVEL[label];

// ── Exposure ranking ─────────────────────────────────────────────────────────
// Spend weighted by how concentrated the set's diversity is: a low-diversity set
// carrying heavy spend has the most at risk. Weighting comes from the label (our
// severity source), which also sidesteps dividing by a zero diversity score.
const LABEL_WEIGHT: Record<DiversityLabelType, number> = {
  [DiversityLabel.LOW]: 1,
  [DiversityLabel.MEDIUM]: 0.5,
  [DiversityLabel.HIGH]: 0.25
};

export const getExposure = (adSet: DiversityAdSet): number =>
  adSet.spend * LABEL_WEIGHT[adSet.diversityLabel];

// ── Delivery status ──────────────────────────────────────────────────────────
// "CAMPAIGN_PAUSED" → "Campaign paused". Meta's raw enum shown in the hover text so the
// distinction between paused, campaign-paused and with-issues isn't lost behind one dot.
const humanizeStatus = (value: string): string => (value.trim() ? humanize(value) : 'Unknown');

export const formatAdSetStatus = (adSet: AdSet): string => {
  if (adSet.status === null) return 'Delivery status not reported for this ad set';
  const { status, effectiveStatus } = adSet.status;
  // Effective status is what's rendered; surface the configured one when they disagree.
  return effectiveStatus === status
    ? humanizeStatus(effectiveStatus)
    : `${humanizeStatus(effectiveStatus)} (set to ${humanizeStatus(status)})`;
};

// ── Platform diversity ───────────────────────────────────────────────────────
export const placementSurface = (position: string): string => {
  const n = position.toLowerCase();
  if (n.includes('reel') || n.includes('story')) return 'story';
  if (n.includes('instream') || n.includes('rewarded') || n === 'classic') return 'video';
  return 'feed';
};

export const computePlatformSpread = (
  placements: { publisher: string; position: string }[]
): PlatformSpread => {
  const real = placements.filter(p => p.publisher !== '');
  if (real.length === 0) {
    return { tier: PlatformTier.BEST, publishers: [], surfaces: [], placementCount: 0 };
  }
  const publishers = [...new Set(real.map(p => p.publisher))].sort();
  const surfaces = [...new Set(real.map(p => placementSurface(p.position)))].sort();
  return {
    tier: publishers.length >= 2 && surfaces.length >= 2 ? PlatformTier.GOOD : PlatformTier.LOW,
    publishers,
    surfaces,
    placementCount: real.length
  };
};

// Ranked so the column sorts Low → Good → Best; unknown sorts below everything.
const PLATFORM_TIER_RANK: Record<PlatformTierType, number> = {
  [PlatformTier.LOW]: 1,
  [PlatformTier.GOOD]: 2,
  [PlatformTier.BEST]: 3
};

export const getPlatformRank = (adSet: DiversityAdSet): number =>
  adSet.platforms === null ? 0 : PLATFORM_TIER_RANK[adSet.platforms.tier];

export const PLATFORM_TIER_LABEL: Record<PlatformTierType, string> = {
  [PlatformTier.LOW]: 'Low',
  [PlatformTier.GOOD]: 'Good',
  [PlatformTier.BEST]: 'Best'
};

const SURFACE_LABEL: Record<string, string> = {
  feed: 'feed',
  story: 'stories & reels',
  video: 'in-stream & other video placements'
};

// Built from observed delivery (which publishers/placements actually ran)
export const formatPlatformDetail = (adSet: DiversityAdSet): string => {
  const spread = adSet.platforms;
  if (spread === null) return 'No placement delivery reported for this ad set.';
  if (spread.tier === PlatformTier.BEST) {
    return 'Automatic placements — Meta reported no specific publisher/platform breakdown for this ad set, so it delivered across the full available surface.';
  }
  const publishers = spread.publishers.join(', ');
  const surfaces = spread.surfaces.map(s => SURFACE_LABEL[s] ?? s).join(' + ');
  return `${publishers} · ${spread.placementCount} placements across ${surfaces}`;
};

// ── Heatmap ────────────────────────────────────────────────────────────────
// Sequential green cell background (light → dark) matching the MetricsTable heat
// mapping. Darker = the metric's best value, so direction flips per metric.
//
// Domains are FITTED TO THE DATA, for the same reason buildRoasBands is: a hand-tuned
// range silently clamps. The previous exposure domain topped out at 110k while this
// export reaches 211k, so the highest-exposure ad set — the row the ranking exists to
// surface — rendered the same shade as everything above the cap. Diversity and fatigue
// clamped too. Fitting the domain guarantees the extremes are always distinguishable.
export interface HeatDomain {
  min: number;
  max: number;
  higherIsBetter: boolean;
}

// Shared by the heat domains and the quadrant's CPX bands.
const percentile = (sorted: number[], fraction: number): number => {
  const index = Math.min(sorted.length - 1, Math.floor(sorted.length * fraction));
  return sorted[index];
};

// Which direction is "good" is a fact about the metric, not something the data can say —
// so it is the only hand-authored part of a domain. Keys double as the lookup type, so a
// renamed metric is a compile error rather than a silently transparent cell.
const HIGHER_IS_BETTER = {
  exposure: false,
  diversity: true,
  fatigue: false,
  roas: true,
  ctr: true,
  cpm: false,
  cpx: false,
  cpc: false,
  kpiVolume: true,
  kpiRevenue: true
} as const;
export type HeatMetricType = keyof typeof HIGHER_IS_BETTER;

type AdMetricKey = 'roas' | 'ctr' | 'cpm' | 'cpx' | 'cpc' | 'kpiVolume' | 'kpiRevenue';

const numeric = (values: (number | null)[]): number[] =>
  values.filter((value): value is number => value !== null && Number.isFinite(value));

// Performance metrics render for ad sets AND for the ads inside them, so both have to
// sit inside the domain or the per-ad rows clamp.
const adSetAndAdValues = (adSets: AdSet[], key: AdMetricKey): number[] =>
  numeric([
    ...adSets.map(adSet => adSet[key]),
    ...adSets.flatMap(adSet => adSet.ads.map(ad => ad[key]))
  ]);

const observeMetrics = (adSets: AdSet[]): Record<HeatMetricType, number[]> => {
  // Scoped to ad sets with a real diversity reading — an NA-diversity ad set has
  // nothing to contribute to either domain, and its diversityScore is null, which
  // can't sit in a number[] anyway.
  const diversityAdSets = getDiversityAdSets(adSets);
  return {
    exposure: diversityAdSets.map(getExposure),
    diversity: diversityAdSets.map(adSet => adSet.diversityScore),
    fatigue: adSets.flatMap(adSet => adSet.fatigueSeries.map(point => point.fatigue)),
    roas: adSetAndAdValues(adSets, 'roas'),
    ctr: adSetAndAdValues(adSets, 'ctr'),
    cpm: adSetAndAdValues(adSets, 'cpm'),
    cpx: adSetAndAdValues(adSets, 'cpx'),
    cpc: adSetAndAdValues(adSets, 'cpc'),
    kpiVolume: adSetAndAdValues(adSets, 'kpiVolume'),
    kpiRevenue: adSetAndAdValues(adSets, 'kpiRevenue')
  };
};

// p5/p95 rather than min/max. Min/max would let a single outlier flatten a whole column:
// one ad at 33% CTR against a median near 1% would render every normal value as the same
// palest shade. Values past either end clamp, which is the right read — "off the scale".
// At small n the percentiles land on the extremes anyway, so nothing is lost there.
const toDomain = (
  observed: Record<HeatMetricType, number[]>,
  metric: HeatMetricType
): HeatDomain => {
  const higherIsBetter = HIGHER_IS_BETTER[metric];
  const values = [...observed[metric]].sort((a, b) => a - b);
  if (values.length === 0) return { min: 0, max: 1, higherIsBetter };
  const min = percentile(values, 0.05);
  const max = percentile(values, 0.95);
  // A single distinct value would divide by zero; widen so it renders at one end.
  return { min, max: max === min ? min + 1 : max, higherIsBetter };
};

export const computeHeatDomains = (adSets: AdSet[]): Record<HeatMetricType, HeatDomain> => {
  const observed = observeMetrics(adSets);
  return {
    exposure: toDomain(observed, 'exposure'),
    diversity: toDomain(observed, 'diversity'),
    fatigue: toDomain(observed, 'fatigue'),
    roas: toDomain(observed, 'roas'),
    ctr: toDomain(observed, 'ctr'),
    cpm: toDomain(observed, 'cpm'),
    cpx: toDomain(observed, 'cpx'),
    cpc: toDomain(observed, 'cpc'),
    kpiVolume: toDomain(observed, 'kpiVolume'),
    kpiRevenue: toDomain(observed, 'kpiRevenue')
  };
};

// Place a metric value on the light→dark green ramp so the metric's best value is darkest —
// which means reading the ramp backwards for a metric where lower is better. Null values
// (unattributed ROAS/CPX, etc.) get no fill.
const SEQUENTIAL_STOPS_MIN_IS_BEST = [...SequentialGreenRamp].reverse();

export const getHeatColor = (
  domains: Record<HeatMetricType, HeatDomain>,
  metricKey: HeatMetricType,
  value: number | null
): string => {
  if (value === null) return 'transparent';
  const { min, max, higherIsBetter } = domains[metricKey];
  return getSteppedHeatColor(
    { min, max, stops: higherIsBetter ? SequentialGreenRamp : SEQUENTIAL_STOPS_MIN_IS_BEST },
    value
  );
};

// ── Formatting ───────────────────────────────────────────────────────────────
export const formatSpend = SPEND_OR_REVENUE_FORMATTER;
export const formatRoas = ROAS_FORMATTER;
export const formatKpiVolume = VOLUME_FORMATTER;
export const formatKpiRevenue = SPEND_OR_REVENUE_FORMATTER;
export const formatCtr = toPrettyPercent2Decimals;

export const getWeightedCpx = (adSets: AdSet[]): number | null => {
  const withValue = adSets.filter(
    (adSet): adSet is AdSet & { kpiVolume: number } =>
      adSet.cpx !== null && adSet.kpiVolume !== null
  );
  const totalVolume = withValue.reduce((sum, adSet) => sum + adSet.kpiVolume, 0);
  if (totalVolume === 0) return null;
  const totalSpend = withValue.reduce((sum, adSet) => sum + adSet.spend, 0);
  return totalSpend / totalVolume;
};

// ── Quadrant ───────────────────────────────────────────────────────────────
// One bubble per ad set that has both a diversity score and a fatigue reading in range.
// There is no weekly playback — only a single reading is shown: y (fatigue) is the
// latest value inside the selected window, while x (diversity) and the bubble's
// spend/CPX are whole-window values. See the date-window note above.
// Plots every ad set that has AT LEAST ONE of the two readings, rather than only the ones
// with both. A missing value is substituted with 0 and flagged as imputed: an ad set with
// no fatigue but 0.3 diversity plots at (0.3, 0), and one with 0.62 fatigue but no
// diversity plots at (0, 0.62). An ad set missing BOTH is still held out — it would sit at
// the origin carrying no information at all.
//
// The imputed flags are what keep this honest. An imputed 0 and a real 0 land on the same
// coordinate, and a real diversity score of 0 is meaningful data (the metrics guide says
// so), so the two have to be distinguishable in the rendering rather than silently merged.
//
// A caveat worth knowing when reading the chart: "no fatigue" here means no fatigue point
// inside the selected date range, which is the same thing the Fatigue table shows as "—".
// So narrowing the date picker can move an ad set onto the y = 0 line. That reads as
// "fatigue fell to zero" when it actually means "no reading in this window" — the imputed
// outline and the tooltip's "N/A" are what tell those apart.
export const buildQuadrantPoints = (adSets: AdSet[], window: DateRange): QuadrantPoint[] =>
  adSets
    .map(adSet => ({
      adSet,
      diversity: adSet.diversityScore,
      fatigue: latestFatigueInWindow(adSet, window)
    }))
    .filter(entry => entry.diversity !== null || entry.fatigue !== null)
    .map(({ adSet, diversity, fatigue }) => ({
      id: adSet.adSetId,
      name: adSet.name,
      x: diversity ?? 0,
      y: fatigue ?? 0,
      spend: adSet.spend,
      cpx: adSet.cpx,
      camp: adSet.campaignIndex,
      label: adSet.diversityLabel,
      imputedX: diversity === null,
      imputedY: fatigue === null
    }));

// CPX bands are scaled to the data rather than fixed — an absolute scale is meaningless
// across accounts. Terciles of the observed values always split the field into thirds, so
// the colour ranks ad sets against their own account. Lower CPX is better, so the CHEAPEST
// tercile is "healthy" (green) and the priciest is "watch" (red) — inverted from a metric
// like ROAS where higher is better.
export interface CpxBands {
  watch: number; // > watch → red
  healthy: number; // <= healthy → green
}

export const buildCpxBands = (points: QuadrantPoint[]): CpxBands => {
  const values = points
    .map(point => point.cpx)
    .filter((cpx): cpx is number => cpx !== null)
    .sort((a, b) => a - b);
  // Under three points terciles are meaningless; treat everything as mid-band.
  if (values.length < 3) return { watch: Infinity, healthy: -Infinity };
  return { healthy: percentile(values, 1 / 3), watch: percentile(values, 2 / 3) };
};

const getCpxColors = (cpx: number | null, bands: CpxBands): { fill: string; stroke: string } => {
  if (cpx === null) return { fill: Neutral700, stroke: Neutral500 };
  if (cpx <= bands.healthy) return { fill: Success900, stroke: Success500 };
  if (cpx <= bands.watch) return { fill: Warning900, stroke: Warning600 };
  return { fill: Danger900, stroke: Danger500 };
};

export const getBubbleColors = (
  point: QuadrantPoint,
  colorMode: ColorModeType,
  bands: CpxBands,
  campaignColors: string[]
): { fill: string; stroke: string } => {
  if (colorMode === ColorMode.CPX) return getCpxColors(point.cpx, bands);
  const color = campaignColors[point.camp];
  const { r, g, b } = hexToRgb(color);
  return { fill: `rgba(${r}, ${g}, ${b}, 0.5)`, stroke: color };
};

// A point needs attention when it is under-diversified (LOW label). Fatigue has no
// agreed cutoff on this scale yet, so it does not flag on its own.
export const isFlaggedQuadrantPoint = (point: QuadrantPoint): boolean =>
  point.label === DiversityLabel.LOW;

// Sort rank for a delivery-status cell, shared by the status column's CellData and by the
// table's own sort. Unknown (-1) ranks below paused (0), which ranks below active (1):
// "we have no status source for this row" is genuinely less informative than "paused",
// so it belongs at the bottom rather than mixed in with real values.
export const statusSortValueFor = (active: boolean | null): number => {
  if (active === null) return -1;
  return active ? 1 : 0;
};

// ── Fatigue table sorting ────────────────────────────────────────────────────
// The fatigue table renders a FLAT row list in which each ad-set row is immediately
// followed by its own expanded ad rows. MetricsTable's built-in sort permutes that flat
// list by a single comparator, which would scatter ad rows away from the ad set they
// belong to — which is why that table passes `sortable={false}`. Sorting is therefore
// owned by the page and applied here, before buildFatigueTableData flattens anything:
// the ad SETS are ordered by the chosen key, and each set's ads are ordered by the same
// key within their parent, so the grouping always survives.

export const FatigueSortKey = {
  NAME: 'name',
  STATUS: 'status',
  FATIGUE: 'fatigue',
  SPEND: 'spend',
  KPI_VOLUME: 'kpiVolume',
  KPI_REVENUE: 'kpiRevenue',
  ROAS: 'roas',
  CPX: 'cpx',
  CPC: 'cpc',
  CPM: 'cpm',
  CTR: 'ctr'
} as const;
export type FatigueSortKeyType = (typeof FatigueSortKey)[keyof typeof FatigueSortKey];

// Order here is the order the dropdown lists them: the two dimensions first, then fatigue
// and spend (the two an analyst reaches for most), then the KPI columns in table order.
export const FATIGUE_SORT_OPTIONS: { value: FatigueSortKeyType; label: string }[] = [
  { value: FatigueSortKey.SPEND, label: 'Spend' },
  { value: FatigueSortKey.FATIGUE, label: 'Fatigue' },
  { value: FatigueSortKey.NAME, label: 'Ad set name' },
  { value: FatigueSortKey.STATUS, label: 'Status' },
  { value: FatigueSortKey.KPI_VOLUME, label: 'KPI Volume' },
  { value: FatigueSortKey.KPI_REVENUE, label: 'KPI Revenue' },
  { value: FatigueSortKey.ROAS, label: 'ROAS' },
  { value: FatigueSortKey.CPX, label: 'CPX' },
  { value: FatigueSortKey.CPC, label: 'CPC' },
  { value: FatigueSortKey.CPM, label: 'CPM' },
  { value: FatigueSortKey.CTR, label: 'CTR' }
];

type SortValue = number | string | null;

// `null` means "this row has no value for this key", which covers two different cases and
// is handled the same way in both: a metric that is genuinely null (no attributed
// revenue, say), and a key that does not exist at this level at all. Fatigue and status
// are ad-SET concepts — there is no per-ad fatigue score and no per-ad status source — so
// for those keys every ad returns null, the ad comparison ties, and the ads fall through
// to the spend tiebreak below. That is deliberate: sorting by fatigue reorders ad sets and
// leaves the ads inside them in their default order rather than inventing an order.
const adSetSortValue = (adSet: AdSet, key: FatigueSortKeyType, window: DateRange): SortValue => {
  switch (key) {
    case FatigueSortKey.NAME:
      return adSet.name;
    case FatigueSortKey.STATUS:
      return statusSortValueFor(adSet.status?.active ?? null);
    case FatigueSortKey.FATIGUE:
      return latestFatigueInWindow(adSet, window);
    case FatigueSortKey.SPEND:
      return adSet.spend;
    default:
      return adSet[key];
  }
};

const adSortValue = (ad: Ad, key: FatigueSortKeyType): SortValue => {
  switch (key) {
    case FatigueSortKey.NAME:
      return ad.name;
    case FatigueSortKey.SPEND:
      return ad.spend;
    case FatigueSortKey.STATUS:
    case FatigueSortKey.FATIGUE:
      return null; // no per-ad source for either — see the note above
    default:
      return ad[key];
  }
};

// Missing values sort LAST in both directions, rather than flipping to the top when the
// direction flips. An ad set with no ROAS is not the "best" or the "worst" ROAS; putting
// it at the end either way keeps the rows a reader cares about at the top.
const compareSortValues = (a: SortValue, b: SortValue, ascending: boolean): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const comparison =
    typeof a === 'string' || typeof b === 'string' ? String(a).localeCompare(String(b)) : a - b;
  return ascending ? comparison : -comparison;
};

export const sortFatigueAdSets = (
  adSets: FatigueAdSet[],
  window: DateRange,
  key: FatigueSortKeyType,
  ascending: boolean
): FatigueAdSet[] => {
  const sortAds = (ads: Ad[]): Ad[] =>
    [...ads].sort((a, b) => {
      const comparison = compareSortValues(adSortValue(a, key), adSortValue(b, key), ascending);
      // Spend descending is the tiebreak at both levels, matching the order this table
      // shipped with, so ties stay stable and never depend on fetch order.
      return comparison !== 0 ? comparison : b.spend - a.spend;
    });

  return [...adSets]
    .sort((a, b) => {
      const comparison = compareSortValues(
        adSetSortValue(a, key, window),
        adSetSortValue(b, key, window),
        ascending
      );
      if (comparison !== 0) return comparison;
      if (b.spend !== a.spend) return b.spend - a.spend;
      return a.name.localeCompare(b.name);
    })
    .map(adSet => ({ ...adSet, ads: sortAds(adSet.ads) }));
};

// ── Fatigue-over-time chart ────────────────────────────────────────────────
export interface FatigueChartRow {
  date: string;
  [adSetId: string]: number | string;
}

// One row per date in the union of the tracked sets' fatigue series; each set's value
// is keyed by its ad-set id. Renders whatever length of history exists.
export const buildFatigueChartData = (adSets: AdSet[], window: DateRange): FatigueChartRow[] => {
  const fatigueAdSets = getFatigueAdSets(adSets);
  const inRange = fatigueAdSets.map(adSet => ({
    adSet,
    points: fatigueInWindow(adSet, window)
  }));
  const dates = [...new Set(inRange.flatMap(e => e.points.map(point => point.date)))].sort();
  const byId = new Map(
    inRange.map(({ adSet, points }) => [
      adSet.adSetId,
      new Map(points.map(point => [point.date, point.fatigue]))
    ])
  );
  return dates.map(date => {
    const row: FatigueChartRow = { date };
    for (const adSet of fatigueAdSets) {
      const value = byId.get(adSet.adSetId)?.get(date);
      if (value !== undefined) {
        row[adSet.adSetId] = value;
      }
    }
    return row;
  });
};

// ── Date-window scoping ──────────────────────────────────────────────────────
// The header date picker only filters what genuinely has a per-day series: the fatigue
// series, daily performance, and launch dates. Spend/ROAS/CTR aggregates are computed
// over the whole export window at build time, and diversity is a single fixed-window
// snapshot with no time dimension at all — neither can honor an arbitrary range, so
// neither is filtered here. See the page CLAUDE.md.
const inWindow = (date: string, { start, end }: DateRange): boolean => date >= start && date <= end;

const fatigueInWindow = (adSet: AdSet, window: DateRange): FatiguePoint[] =>
  adSet.fatigueSeries.filter(point => inWindow(point.date, window));

// The most recent fatigue reading inside the window (null when the set has none).
export const latestFatigueInWindow = (adSet: AdSet, window: DateRange): number | null => {
  const points = fatigueInWindow(adSet, window);
  return points.length ? points[points.length - 1].fatigue : null;
};

export const launchesInWindow = (adSet: AdSet, window: DateRange): LaunchEvent[] =>
  adSet.launches.filter(launch => inWindow(launch.date, window));

// ── Ad launch markers ────────────────────────────────────────────────────────
// Launch dates are inferred from the delivery data (an ad's first day with spend) —
// no export carries a real launch date. Ads already running when the export window
// opens are left-censored and carry no launch date, so they never appear here.

// "2026-07-15" → "7/15". Shared with both chart axes so tick and tooltip agree.
export const shortDate = (date: string): string => {
  const [, month, day] = date.split('-');
  return `${Number(month)}/${Number(day)}`;
};

// A big launch day would otherwise produce an unreadable hover tooltip.
const MAX_TOOLTIP_NAMES = 6;

// Takes a prebuilt id → name map rather than the ad set: callers render one marker per
// launch day, so building the map here would rebuild it per marker. Names come from the
// roster rather than the event so the local real-name overlay reaches these tooltips.
export const formatLaunchTooltip = (launch: LaunchEvent, adNames: Map<string, string>): string => {
  const shown = launch.adIds.slice(0, MAX_TOOLTIP_NAMES).map(id => adNames.get(id) ?? id);
  const remaining = launch.count - shown.length;
  const heading = `${conditionallyPluralizeWithCount(launch.count, 'ad')} launched · ${shortDate(
    launch.date
  )}`;
  const more = remaining > 0 ? `\n+${remaining} more` : '';
  return `${heading}\n${shown.join('\n')}${more}`;
};

// ── Focus chart (single isolated ad set) ─────────────────────────────────────
export interface FocusChartRow {
  date: string;
  fatigue: number;
  cpx: number | null;
  ctr: number | null;
}

// Fatigue series merged with daily performance by date. Diversity has only a current
// snapshot (no daily series), so it is not plotted here.
export const buildFocusChartData = (adSet: AdSet, window: DateRange): FocusChartRow[] => {
  const perfByDate = new Map(adSet.perfDaily.map(point => [point.date, point]));
  return fatigueInWindow(adSet, window).map(point => ({
    date: point.date,
    fatigue: point.fatigue,
    cpx: perfByDate.get(point.date)?.cpx ?? null,
    ctr: perfByDate.get(point.date)?.ctr ?? null
  }));
};

// ── Alerts ───────────────────────────────────────────────────────────────────
// Only diversity alerts are derivable from the real data: the LOW-label ad sets.
export const getLowDiversitySets = (adSets: AdSet[]): DiversityAdSet[] =>
  getDiversityAdSets(adSets).filter(adSet => adSet.diversityLabel === DiversityLabel.LOW);

export const buildAlerts = (adSets: AdSet[]): Alert[] => {
  const lowDiversitySets = getLowDiversitySets(adSets);
  if (lowDiversitySets.length === 0) return [];
  return [
    {
      id: 'diversity-low',
      category: AlertCategory.DIVERSITY,
      summary: `${conditionallyPluralizeWithCount(lowDiversitySets.length, 'ad set')} ${
        lowDiversitySets.length === 1 ? 'is' : 'are'
      } low-diversity (${formatSpend(
        lowDiversitySets.reduce((sum, adSet) => sum + adSet.spend, 0)
      )} combined) — a few creatives carry most of their spend.`,
      adSets: lowDiversitySets
    }
  ];
};
